import { useEffect, useMemo, useRef, useState } from "react";
import { t } from "../../../lib/i18n";
import { Card, Screen, StudyGate } from "../kit";
import { Burst, DeckPicker, GameHeader, Result, rnd, shuffle, useQ, type DeckQ } from "./common";

/* ── Pengembaraan Soalan: hop across the map, one right answer per step ──── */
const STEPS = 10;
const AVATARS = ["🦊", "🐼", "🐯", "🐸", "🐰", "🦁"];

/** Zig-zag path of stepping stones, castle at the end; the avatar sits on the current stone. */
function QuestMap({ at, avatar }: { at: number; avatar: string }) {
  const pts = Array.from({ length: STEPS + 1 }, (_, i) => ({ x: [50, 74, 50, 26][i % 4]!, y: 92 - i * 8.6 }));
  return (
    <div className="relative mx-auto h-72 w-full max-w-sm overflow-hidden rounded-3xl bg-gradient-to-b from-sky-300 via-emerald-200 to-lime-200 shadow-inner ring-1 ring-black/5" aria-hidden>
      <span className="absolute left-3 top-2 text-2xl">☁️</span><span className="absolute right-6 top-8 text-xl">☁️</span>
      <span className="absolute bottom-6 left-3 text-2xl">🌳</span><span className="absolute bottom-16 right-3 text-2xl">🌲</span><span className="absolute left-4 top-1/2 text-xl">🌼</span>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        <polyline points={pts.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#A16207" strokeWidth="1.6" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
      </svg>
      {pts.map((p, i) => (
        <span key={i} className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full ${i === STEPS ? "text-3xl" : `h-5 w-5 ${i < at ? "bg-amber-400" : "bg-white/90"} ring-2 ring-amber-600/40`}`}
          style={{ left: `${p.x}%`, top: `${p.y}%` }}>{i === STEPS ? "🏰" : ""}</span>
      ))}
      <span key={at} className="absolute -translate-x-1/2 -translate-y-full animate-hop text-4xl transition-all duration-500" style={{ left: `${pts[at]!.x}%`, top: `${pts[at]!.y}%` }}>{avatar}</span>
    </div>
  );
}

function AnswerGrid({ opts, right, picked, onPick }: { opts: string[]; right: number; picked: number | null; onPick: (k: number) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {opts.map((o, k) => {
        const st = picked === null ? "ring-black/5" : k === right ? "bg-emerald-500 text-white ring-emerald-500" : k === picked ? "animate-shake bg-rose-500 text-white ring-rose-500" : "opacity-50 ring-black/5";
        return (
          <button key={k} type="button" disabled={picked !== null} onClick={() => onPick(k)}
            className={`flex min-h-[4.5rem] flex-col items-center justify-center gap-1 rounded-2xl bg-card p-3 text-center text-sm font-semibold leading-snug text-slate-800 shadow-sm ring-2 transition ${st}`}>
            <span className="grid h-6 w-6 place-items-center rounded-full bg-black/10 text-xs font-extrabold">{"ABCD"[k]}</span>
            <span className="break-words">{o}</span>
          </button>
        );
      })}
    </div>
  );
}

export function QuestPage() { return <Screen><StudyGate><Quest /></StudyGate></Screen>; }
function Quest() {
  const { stem, opts, why } = useQ();
  const [deck, setDeck] = useState<DeckQ[] | null>(null);
  const [label, setLabel] = useState("");
  const [i, setI] = useState(0);
  const [at, setAt] = useState(0);
  const [lives, setLives] = useState(3);
  const [picked, setPicked] = useState<number | null>(null);
  const [burst, setBurst] = useState(0);
  const avatar = useMemo(() => AVATARS[rnd(0, AVATARS.length - 1)]!, [deck]);
  const header = <GameHeader title={t("Pengembaraan Soalan")} emoji="🗺️" tone="from-lime-500 to-emerald-600" score={deck ? at : undefined} lives={deck ? lives : undefined} progress={deck ? at / STEPS : undefined} />;

  if (!deck) {
    return (
      <>
        {header}
        <Card className="space-y-4 text-center">
          <p className="animate-bob text-6xl" aria-hidden>🗺️</p>
          <h1 className="text-2xl font-extrabold text-slate-900">{t("Pengembaraan Soalan")}</h1>
          <p className="text-sm text-slate-500">{t("Jawab betul untuk melompat ke batu seterusnya. Sampai ke istana sebelum nyawa habis!")}</p>
          <DeckPicker onDeck={(qs, l) => { setDeck(qs); setLabel(l); setI(0); setAt(0); setLives(3); setPicked(null); }} />
        </Card>
      </>
    );
  }
  const finished = at >= STEPS || lives <= 0 || i >= deck.length;
  if (finished) return <>{header}{at >= STEPS && <p className="mb-3 animate-rise text-center text-5xl">🏰🎉</p>}<Result game="quest" score={at} max={STEPS} onAgain={() => setDeck(null)} details={{ unit: label.slice(0, 80) }} /></>;

  const q = deck[i]!;
  const o = opts(q);
  function pick(k: number) {
    if (picked !== null) return;
    setPicked(k);
    if (k === q.answer) { setBurst((b) => b + 1); setTimeout(() => { setAt((a) => a + 1); setI((x) => x + 1); setPicked(null); }, 900); }
    else setLives((l) => l - 1);
  }
  return (
    <>
      {header}
      <Burst show={burst} />
      <p className="mb-2 truncate text-center text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <QuestMap at={at} avatar={avatar} />
      <div key={q.id} className="mt-4 animate-rise space-y-3">
        <Card><p className="text-base font-extrabold leading-snug text-slate-900">{stem(q)}</p></Card>
        <AnswerGrid opts={o} right={q.answer} picked={picked} onPick={pick} />
        {picked !== null && picked !== q.answer && (
          <div className="animate-rise space-y-2">
            {why(q) && <p className="rounded-2xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">{why(q)}</p>}
            <button type="button" onClick={() => { setI((x) => x + 1); setPicked(null); }} className="w-full rounded-full bg-violet-500 py-3 font-extrabold text-white shadow-md">{t("Faham! Seterusnya →")}</button>
          </div>
        )}
      </div>
    </>
  );
}

/* ── Padan Kad: memory game, question ↔ answer ───────────────────────────── */
type MCard = { id: string; pair: string; text: string; kind: "q" | "a" };
const PAIRS = 6;

export function PadanPage() { return <Screen><StudyGate><Padan /></StudyGate></Screen>; }
function Padan() {
  const { stem, opts } = useQ();
  const [cards, setCards] = useState<MCard[] | null>(null);
  const [open, setOpen] = useState<string[]>([]);
  const [found, setFound] = useState<Set<string>>(new Set());
  const [moves, setMoves] = useState(0);
  const [burst, setBurst] = useState(0);
  const [label, setLabel] = useState("");
  const [err, setErr] = useState<string | null>(null);

  function deal(qs: DeckQ[], l: string) {
    // Short question + answer pairs read best on small cards.
    const good = qs.filter((q) => stem(q).length <= 110 && (opts(q)[q.answer] ?? "").length <= 45);
    const chosen = (good.length >= PAIRS ? good : qs).slice(0, PAIRS);
    if (chosen.length < 3) { setErr(t("Tiada soalan untuk pilihan ini lagi.")); return; }
    setCards(shuffle(chosen.flatMap((q) => [
      { id: `${q.id}-q`, pair: q.id, text: stem(q), kind: "q" as const },
      { id: `${q.id}-a`, pair: q.id, text: opts(q)[q.answer] ?? "", kind: "a" as const },
    ])));
    setOpen([]); setFound(new Set()); setMoves(0); setLabel(l); setErr(null);
  }
  function flip(c: MCard) {
    if (!cards || open.length === 2 || open.includes(c.id) || found.has(c.pair)) return;
    const next = [...open, c.id];
    setOpen(next);
    if (next.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = next.map((id) => cards.find((x) => x.id === id)!);
      if (a!.pair === b!.pair) { setTimeout(() => { setFound((f) => new Set(f).add(a!.pair)); setOpen([]); setBurst((x) => x + 1); }, 450); }
      else setTimeout(() => setOpen([]), 1100);
    }
  }
  const pairs = cards ? cards.length / 2 : PAIRS;
  const header = <GameHeader title={t("Padan Kad")} emoji="🃏" tone="from-sky-500 to-indigo-500" score={cards ? found.size : undefined} progress={cards ? found.size / pairs : undefined} />;

  if (!cards) {
    return (
      <>
        {header}
        <Card className="space-y-4 text-center">
          <p className="animate-bob text-6xl" aria-hidden>🃏</p>
          <h1 className="text-2xl font-extrabold text-slate-900">{t("Padan Kad")}</h1>
          <p className="text-sm text-slate-500">{t("Terbalikkan dua kad. Padankan setiap soalan (biru) dengan jawapannya (kuning).")}</p>
          <DeckPicker onDeck={deal} />
          {err && <p className="text-sm font-semibold text-rose-600">{err}</p>}
        </Card>
      </>
    );
  }
  if (found.size === pairs) {
    const extra = Math.max(0, moves - pairs);
    const score = Math.max(1, pairs - Math.floor(extra / 2));
    return <>{header}<p className="mb-3 text-center text-sm font-bold text-slate-600">{t("{n} langkah", { n: moves })}</p><Result game="padan" score={score} max={pairs} onAgain={() => setCards(null)} details={{ moves, unit: label.slice(0, 80) }} /></>;
  }
  return (
    <>
      {header}
      <Burst show={burst} emoji={["🃏", "⭐", "✨"]} />
      <p className="mb-2 text-center text-sm font-bold text-slate-500">{t("{n} langkah", { n: moves })}</p>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" style={{ perspective: "800px" }}>
        {cards.map((c) => {
          const up = open.includes(c.id) || found.has(c.pair);
          return (
            <button key={c.id} type="button" onClick={() => flip(c)} aria-label={up ? c.text : t("Kad tertutup")}
              className={`card-3d relative h-28 w-full sm:h-32 ${up ? "flipped" : ""}`}>
              <span className="card-face absolute inset-0 grid place-items-center rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-3xl text-white shadow-md">❓</span>
              <span className={`card-face card-back absolute inset-0 flex items-center justify-center overflow-hidden rounded-2xl p-2 text-center text-[11px] font-semibold leading-tight shadow-md sm:text-xs ${found.has(c.pair) ? "bg-emerald-100 text-emerald-900 ring-2 ring-emerald-400" : c.kind === "q" ? "bg-sky-100 text-sky-900" : "bg-amber-100 text-amber-900"}`}>
                <span className="line-clamp-6">{c.text}</span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ── Betul atau Salah Kilat: 60 seconds, judge each answer ───────────────── */
const SECONDS = 60;

export function BetulSalahPage() { return <Screen><StudyGate><BetulSalah /></StudyGate></Screen>; }
function BetulSalah() {
  const { stem, opts } = useQ();
  const [deck, setDeck] = useState<DeckQ[] | null>(null);
  const [i, setI] = useState(0);
  const [shown, setShown] = useState<{ text: string; truth: boolean } | null>(null);
  const [score, setScore] = useState(0);
  const [tries, setTries] = useState(0);
  const [left, setLeft] = useState(SECONDS);
  const [flash, setFlash] = useState<"ok" | "no" | null>(null);
  const [burst, setBurst] = useState(0);
  const [label, setLabel] = useState("");
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const deal = (d: DeckQ[], k: number) => {
    const q = d[k % d.length]!;
    const o = opts(q);
    const truth = Math.random() < 0.5;
    const wrong = o.map((_, j) => j).filter((j) => j !== q.answer);
    setShown({ text: o[truth ? q.answer : wrong[rnd(0, wrong.length - 1)]!] ?? "", truth });
  };
  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);
  function start(d: DeckQ[], l: string) {
    setDeck(d); setLabel(l); setI(0); setScore(0); setTries(0); setLeft(SECONDS); setFlash(null); deal(d, 0);
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => setLeft((s) => { if (s <= 1 && timer.current) clearInterval(timer.current); return Math.max(0, s - 1); }), 1000);
  }
  function judge(says: boolean) {
    if (!deck || !shown || flash) return;
    const ok = says === shown.truth;
    setTries((n) => n + 1); setFlash(ok ? "ok" : "no");
    if (ok) { setScore((s) => s + 1); setBurst((b) => b + 1); }
    const nextI = i + 1;
    setTimeout(() => { setFlash(null); setI(nextI); deal(deck, nextI); }, ok ? 350 : 900);
  }
  const header = <GameHeader title={t("Betul atau Salah Kilat")} emoji="⚡" tone="from-fuchsia-500 to-rose-500" score={deck ? score : undefined} progress={deck ? left / SECONDS : undefined} />;

  if (!deck) {
    return (
      <>
        {header}
        <Card className="space-y-4 text-center">
          <p className="animate-bob text-6xl" aria-hidden>⚡</p>
          <h1 className="text-2xl font-extrabold text-slate-900">{t("Betul atau Salah Kilat")}</h1>
          <p className="text-sm text-slate-500">{t("Adakah jawapan ini betul? Jawab sebanyak mungkin dalam 60 saat!")}</p>
          <DeckPicker onDeck={start} />
        </Card>
      </>
    );
  }
  if (left <= 0) return <>{header}<Result game="betulsalah" score={score} max={Math.max(1, tries)} onAgain={() => setDeck(null)} details={{ tries, unit: label.slice(0, 80) }} /></>;
  const q = deck[i % deck.length]!;
  return (
    <>
      {header}
      <Burst show={burst} emoji={["⚡", "⭐"]} />
      <p className="mb-3 text-center text-3xl font-black text-slate-900">⏱ {left}s</p>
      <div key={i} className={`animate-rise rounded-3xl bg-card p-5 shadow-md ring-4 ${flash === "ok" ? "ring-emerald-400" : flash === "no" ? "animate-shake ring-rose-400" : "ring-transparent"}`}>
        <p className="text-base font-bold leading-snug text-slate-700">{stem(q)}</p>
        <p className="mt-4 rounded-2xl bg-violet-50 p-4 text-center text-xl font-extrabold text-violet-800">{shown?.text}</p>
        {flash === "no" && <p className="mt-2 text-center text-sm font-bold text-rose-600">{shown?.truth ? t("Itu sebenarnya betul!") : t("Jawapan betul: {x}", { x: opts(q)[q.answer] ?? "" })}</p>}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <button type="button" onClick={() => judge(false)} className="rounded-3xl bg-rose-500 py-5 text-2xl font-black text-white shadow-lg active:scale-95">✘ {t("Salah")}</button>
        <button type="button" onClick={() => judge(true)} className="rounded-3xl bg-emerald-500 py-5 text-2xl font-black text-white shadow-lg active:scale-95">✔ {t("Betul")}</button>
      </div>
    </>
  );
}
